
import { useState, useEffect } from 'react';
import type { Step, StepAnswer } from '../../../types/step';

interface StepModalProps {
  step: Step;
  answers?: StepAnswer;
  onClose: () => void;
  onComplete: (stepId: number, answers: StepAnswer) => void;
}

export default function StepModal({ step, answers, onClose, onComplete }: StepModalProps) {
  const [responses, setResponses] = useState<string[]>([]);

  useEffect(() => {
    if (answers) {
      setResponses(answers.responses);
    } else {
      setResponses(new Array(step.questions.length).fill(''));
    }
  }, [step, answers]);

  const handleResponseChange = (index: number, value: string) => {
    const newResponses = [...responses];
    newResponses[index] = value;
    setResponses(newResponses);
  };

  const handleComplete = () => {
    const allAnswered = responses.every(r => r.trim() !== '');
    if (!allAnswered) {
      alert('Пожалуйста, ответьте на все вопросы');
      return;
    }

    onComplete(step.id, {
      stepId: step.id,
      responses
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] p-6 rounded-t-3xl">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-white/80 text-sm mb-1">Шаг {step.id} из 15</div>
              <h2 className="text-2xl font-bold text-white">{step.title}</h2>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
          <p className="text-white/90 mt-3 leading-relaxed">{step.description}</p>
        </div>

        {/* Questions */}
        <div className="p-6 space-y-6">
          {step.questions.map((question, index) => (
            <div key={question.id}>
              <label className="block text-gray-900 font-medium mb-3">
                {question.text}
              </label>
              <textarea
                value={responses[index] || ''}
                onChange={(e) => handleResponseChange(index, e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-2xl focus:border-[#F9659C] focus:outline-none resize-none transition-colors text-base"
                rows={4}
                placeholder="Напиши свои мысли здесь..."
              />
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-100 p-6 rounded-b-3xl flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-200 rounded-2xl font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            Закрыть
          </button>
          <button
            onClick={handleComplete}
            className="flex-1 px-6 py-3 bg-gradient-to-r from-[#F9659C] to-[#ACE0EB] text-white rounded-2xl font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
          >
            Выполнено
          </button>
        </div>
      </div>
    </div>
  );
}
